"use client";

import { useCallback, useMemo, useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";

const NON_WORD_RE = /[^\w\s]/g;
const WHITESPACE_RE = /\s+/;

export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(NON_WORD_RE, "")
    .split(WHITESPACE_RE)
    .filter((w) => w.length > 0);
}

function getSharedWords(a: string[], b: string[]): Set<string> {
  const setA = new Set(a);
  const setB = new Set(b);
  const shared = new Set<string>();
  for (const word of setA) {
    if (setB.has(word)) {
      shared.add(word);
    }
  }
  return shared;
}

/**
 * Maximal runs of at least `minLength` consecutive words that appear in both
 * texts. A shared 5-word run is one phrase, not 5 + 4 + 3 overlapping n-grams:
 * runs are found along each alignment, then spans that overlap in the paraphrase
 * are merged and counted once.
 */
export function findMatchingPhrases(
  original: string[],
  paraphrase: string[],
  minLength: number
): { phrase: string; indices: number[] }[] {
  const spans: [number, number][] = [];
  let previous = new Uint32Array(paraphrase.length + 1);
  for (let i = 0; i < original.length; i++) {
    const current = new Uint32Array(paraphrase.length + 1);
    for (let j = 0; j < paraphrase.length; j++) {
      if (original[i] !== paraphrase[j]) {
        continue;
      }
      const run = previous[j] + 1;
      current[j + 1] = run;
      const continues =
        i + 1 < original.length &&
        j + 1 < paraphrase.length &&
        original[i + 1] === paraphrase[j + 1];
      if (run >= minLength && !continues) {
        spans.push([j - run + 1, j + 1]);
      }
    }
    previous = current;
  }

  spans.sort((a, b) => a[0] - b[0] || b[1] - a[1]);
  const merged: [number, number][] = [];
  for (const [start, end] of spans) {
    const last = merged.at(-1);
    if (last && start < last[1]) {
      last[1] = Math.max(last[1], end);
    } else {
      merged.push([start, end]);
    }
  }

  return merged.map(([start, end]) => ({
    phrase: paraphrase.slice(start, end).join(" "),
    indices: Array.from({ length: end - start }, (_, k) => start + k),
  }));
}

export function computeOverlap(
  original: string[],
  paraphrase: string[]
): number {
  const allUnique = new Set([...original, ...paraphrase]);
  if (allUnique.size === 0) {
    return 0;
  }
  const shared = getSharedWords(original, paraphrase);
  return Math.round((shared.size / allUnique.size) * 100);
}

export function computeSimilarityScore(
  overlapPercent: number,
  matchingPhraseCount: number
): number {
  // Weighted: 60% word overlap + 40% phrase penalty
  const phrasePenalty = Math.min(matchingPhraseCount * 10, 40);
  return Math.min(Math.round(overlapPercent * 0.6 + phrasePenalty), 100);
}

function getScoreColor(score: number): string {
  if (score < 30) {
    return "text-green-600 dark:text-green-400";
  }
  if (score <= 50) {
    return "text-yellow-600 dark:text-yellow-400";
  }
  return "text-red-600 dark:text-red-400";
}

function getScoreBg(score: number): string {
  if (score < 30) {
    return "border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950/30";
  }
  if (score <= 50) {
    return "border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-950/30";
  }
  return "border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/30";
}

/** English strings. A translated content file overrides these through `ui`. */
export const PARAPHRASE_CHECKER_STRINGS = {
  originalText: "Original Text",
  pasteTheOriginalTextHere: "Paste the original text here...",
  wordsCount: "one:{count} word|other:{count} words",
  yourParaphrase: "Your Paraphrase",
  pasteYourParaphraseHere: "Paste your paraphrase here...",
  compare: "Compare",
  overallSimilarityScore: "Overall Similarity Score",
  wordOverlap: "Word Overlap",
  sharedWordsTotalUniqueWords: "Shared words / total unique words",
  matchingPhrases: "Matching Phrases",
  sequencesOf3ConsecutiveMatching: "Sequences of 3+ consecutive matching words",
  highlightedMatchesInYourParaphrase: "Highlighted Matches in Your Paraphrase",
  highlightedWordsArePartOf:
    "Highlighted words are part of matching phrases found in the original text.",
  matchingPhrasesFound: "Matching Phrases Found",
  text: "“",
  text2: "”",
  tipsForEffectiveParaphrasing: "Tips for Effective Paraphrasing",
  readAndUnderstandFirst: "Read and understand first",
  putTheOriginalAwayAnd: " — Put the original away and write from memory.",
  changeSentenceStructure: "Change sentence structure",
  rearrangeClausesCombineOrSplit:
    " — Rearrange clauses, combine or split sentences.",
  useSynonyms: "Use synonyms",
  replaceKeyWordsWithAlternatives:
    " — Replace key words with alternatives (but keep technical terms).",
  changeVoice: "Change voice",
  switchBetweenActiveAndPassive: " — Switch between active and passive voice.",
  alwaysCite: "Always cite",
  evenAPerfectParaphraseNeeds:
    " — Even a perfect paraphrase needs an in-text citation.",
  goodParaphrase: "Good paraphrase",
  needsMoreWork: "Needs more work",
  tooSimilarToOriginal: "Too similar to original",
} as const;

type Strings = typeof PARAPHRASE_CHECKER_STRINGS;

function getScoreLabel(score: number, strings: Strings): string {
  if (score < 30) {
    return strings.goodParaphrase;
  }
  if (score <= 50) {
    return strings.needsMoreWork;
  }
  return strings.tooSimilarToOriginal;
}

export default function ParaphraseCheckerWidget() {
  const [original, setOriginal] = useState("");
  const [paraphrase, setParaphrase] = useState("");
  const [hasCompared, setHasCompared] = useState(false);

  const results = useMemo(() => {
    if (!(hasCompared && original.trim() && paraphrase.trim())) {
      return null;
    }

    const origTokens = tokenize(original);
    const paraTokens = tokenize(paraphrase);
    const overlap = computeOverlap(origTokens, paraTokens);
    const matchingPhrases = findMatchingPhrases(origTokens, paraTokens, 3);
    const score = computeSimilarityScore(overlap, matchingPhrases.length);

    return { overlap, matchingPhrases, score, paraTokens };
  }, [original, paraphrase, hasCompared]);

  const handleCompare = useCallback(() => {
    setHasCompared(true);
  }, []);

  const highlightedParaphrase = useMemo(() => {
    if (!results) {
      return null;
    }

    const { matchingPhrases, paraTokens } = results;
    const highlightedIndices = new Set<number>();

    for (const match of matchingPhrases) {
      for (const idx of match.indices) {
        highlightedIndices.add(idx);
      }
    }

    return paraTokens.map((word, i) => ({
      word,
      highlighted: highlightedIndices.has(i),
    }));
  }, [results]);

  const strings = useToolUi(PARAPHRASE_CHECKER_STRINGS);

  const fmt = useToolNumber();
  const locale = useToolLocale();

  return (
    <>
      {/* Side-by-side textareas */}
      <section className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="original-text"
          >
            {strings.originalText}
          </label>
          <textarea
            className="min-h-[200px] w-full rounded-lg border border-border bg-card px-4 py-3 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="original-text"
            onChange={(e) => {
              setOriginal(e.target.value);
              setHasCompared(false);
            }}
            placeholder={strings.pasteTheOriginalTextHere}
            value={original}
          />
          <p className="text-muted-foreground text-xs">
            {tPlural(strings.wordsCount, locale, tokenize(original).length, {
              count: fmt(tokenize(original).length),
            })}
          </p>
        </div>
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="paraphrase-text"
          >
            {strings.yourParaphrase}
          </label>
          <textarea
            className="min-h-[200px] w-full rounded-lg border border-border bg-card px-4 py-3 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="paraphrase-text"
            onChange={(e) => {
              setParaphrase(e.target.value);
              setHasCompared(false);
            }}
            placeholder={strings.pasteYourParaphraseHere}
            value={paraphrase}
          />
          <p className="text-muted-foreground text-xs">
            {tPlural(strings.wordsCount, locale, tokenize(paraphrase).length, {
              count: fmt(tokenize(paraphrase).length),
            })}
          </p>
        </div>
      </section>
      {/* Compare button */}
      <div>
        <button
          className="rounded-lg bg-primary px-6 py-2.5 font-medium text-primary-foreground text-sm transition-colors hover:bg-primary/90 disabled:opacity-50"
          disabled={!(original.trim() && paraphrase.trim())}
          onClick={handleCompare}
          type="button"
        >
          {strings.compare}
        </button>
      </div>
      {/* Results */}
      {results && (
        <>
          {/* Score card */}
          <section
            className={`rounded-lg border p-6 text-center ${getScoreBg(results.score)}`}
          >
            <p className="mb-1 text-muted-foreground text-sm">
              {strings.overallSimilarityScore}
            </p>
            <p className={`font-bold text-4xl ${getScoreColor(results.score)}`}>
              {results.score}%
            </p>
            <p
              className={`mt-2 font-medium text-sm ${getScoreColor(results.score)}`}
            >
              {getScoreLabel(results.score, strings)}
            </p>
          </section>

          {/* Metrics */}
          <section className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-border bg-card p-5">
              <p className="mb-1 text-muted-foreground text-xs uppercase tracking-wide">
                {strings.wordOverlap}
              </p>
              <p className="font-semibold text-2xl text-foreground">
                {results.overlap}%
              </p>
              <p className="mt-1 text-muted-foreground text-xs">
                {strings.sharedWordsTotalUniqueWords}
              </p>
            </div>
            <div className="rounded-lg border border-border bg-card p-5">
              <p className="mb-1 text-muted-foreground text-xs uppercase tracking-wide">
                {strings.matchingPhrases}
              </p>
              <p className="font-semibold text-2xl text-foreground">
                {results.matchingPhrases.length}
              </p>
              <p className="mt-1 text-muted-foreground text-xs">
                {strings.sequencesOf3ConsecutiveMatching}
              </p>
            </div>
          </section>

          {/* Highlighted paraphrase */}
          {highlightedParaphrase && highlightedParaphrase.length > 0 && (
            <section className="space-y-3">
              <h2 className="font-semibold text-foreground text-sm">
                {strings.highlightedMatchesInYourParaphrase}
              </h2>
              <div className="rounded-lg border border-border bg-card p-5">
                <p className="text-foreground text-sm leading-relaxed">
                  {highlightedParaphrase.map((item, i) => (
                    <span key={`${item.word}-${i}`}>
                      {i > 0 && " "}
                      {item.highlighted ? (
                        <mark className="rounded bg-red-200 px-0.5 dark:bg-red-900/50">
                          {item.word}
                        </mark>
                      ) : (
                        item.word
                      )}
                    </span>
                  ))}
                </p>
              </div>
              <p className="text-muted-foreground text-xs">
                {strings.highlightedWordsArePartOf}
              </p>
            </section>
          )}

          {/* Matching phrases list */}
          {results.matchingPhrases.length > 0 && (
            <section className="space-y-3">
              <h2 className="font-semibold text-foreground text-sm">
                {strings.matchingPhrasesFound}
              </h2>
              <ul className="space-y-2">
                {results.matchingPhrases.map((match) => (
                  <li
                    className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-red-700 text-sm dark:border-red-800 dark:bg-red-950/30 dark:text-red-300"
                    key={match.phrase}
                  >
                    {strings.text}
                    {match.phrase}
                    {strings.text2}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
      {/* Tips */}
      <section className="rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="mb-4 font-semibold text-foreground text-sm">
          {strings.tipsForEffectiveParaphrasing}
        </h2>
        <ul className="space-y-2 text-muted-foreground text-sm">
          <li>
            <strong>{strings.readAndUnderstandFirst}</strong>
            {strings.putTheOriginalAwayAnd}
          </li>
          <li>
            <strong>{strings.changeSentenceStructure}</strong>
            {strings.rearrangeClausesCombineOrSplit}
          </li>
          <li>
            <strong>{strings.useSynonyms}</strong>
            {strings.replaceKeyWordsWithAlternatives}
          </li>
          <li>
            <strong>{strings.changeVoice}</strong>
            {strings.switchBetweenActiveAndPassive}
          </li>
          <li>
            <strong>{strings.alwaysCite}</strong>
            {strings.evenAPerfectParaphraseNeeds}
          </li>
        </ul>
      </section>
      {/* Related tools */}
    </>
  );
}
